import React, { useRef, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Upload,
  Download,
  FileSpreadsheet,
  Check,
  AlertCircle,
  ArrowRight,
  ChevronLeft,
  RefreshCw,
} from 'lucide-react';

// ─── Header normalization ────────────────────────────────────────────────────
function normalize(str) {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/[^a-z0-9\s]/g, '')    // remove non-alphanumeric
    .replace(/\s+/g, ' ')
    .trim();
}

// ─── Excel row background color → lead status ────────────────────────────────
// Returns null when no color info is available (let ESTATUS column be the fallback).
// Returns a status string when a definite fill color is detected.
function mapExcelRowColorToLeadStatus(rgb) {
  if (!rgb) return null;
  // Strip ARGB alpha prefix (e.g. "FFFF0000" → "FF0000")
  const hex = rgb.length === 8 ? rgb.slice(2) : rgb;
  if (hex.length !== 6) return null;
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (l > 0.92) return 'no_contactado'; // explicit white fill → Sin contactar
  const d = max - min;
  if (d < 0.1) return 'no_contactado'; // near gray / achromatic → Sin contactar
  let h = 0;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = Math.round(h * 60);
  if (h < 0) h += 360;
  if ((h >= 0 && h <= 25) || h >= 340) return 'rechazado';   // red
  if (h >= 40 && h <= 75) return 'en_proceso';               // yellow
  if (h >= 85 && h <= 165) return 'trato_cerrado';           // green
  return 'no_contactado';
}

// ─── Month detection for sales-reports ──────────────────────────────────────
const MONTH_NAMES_ES = [
  'enero','febrero','marzo','abril','mayo','junio',
  'julio','agosto','septiembre','octubre','noviembre','diciembre'
];

function detectMonthFromText(text) {
  if (!text) return '';
  const str = String(text).toLowerCase();
  for (let i = 0; i < MONTH_NAMES_ES.length; i++) {
    if (str.includes(MONTH_NAMES_ES[i])) {
      const yearMatch = String(text).match(/\d{4}/);
      const year = yearMatch ? yearMatch[0] : new Date().getFullYear();
      const label = MONTH_NAMES_ES[i].charAt(0).toUpperCase() + MONTH_NAMES_ES[i].slice(1);
      return `${label} ${year}`;
    }
  }
  return '';
}

function detectReportMonth(ws, sheetName) {
  // Priority 1: sheet name contains a month word
  const fromName = detectMonthFromText(sheetName);
  if (fromName) return fromName;
  // Priority 2: scan metadata rows 0-6 for month words
  try {
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
    for (let i = 0; i < Math.min(rows.length, 7); i++) {
      for (const cell of (rows[i] || [])) {
        const found = detectMonthFromText(cell);
        if (found) return found;
      }
    }
  } catch (_) {}
  return sheetName; // fallback: use sheet name as-is
}

// ─── Excel date serial / string → YYYY-MM-DD ─────────────────────────────────
function formatExcelDate(val) {
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return null;
    const y = val.getUTCFullYear();
    const m = String(val.getUTCMonth() + 1).padStart(2, '0');
    const d = String(val.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof val === 'number') {
    // Excel serial: 1 = Jan 1 1900 (Lotus 1-2-3 leap-year bug offset)
    const date = new Date(1899, 11, 30 + val);
    if (isNaN(date.getTime())) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(val).trim();
  // DD/MM/YYYY (formato español / Allianz Excel) — parse manual, sin new Date()
  const dmyMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmyMatch) {
    const [, dd, mm, yyyy] = dmyMatch;
    return `${yyyy}-${mm.padStart(2,'0')}-${dd.padStart(2,'0')}`;
  }
  // ISO YYYY-MM-DD o con sufijo de tiempo (e.g. "2018-04-30T00:00:00")
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
  // M/D/YY (American short format — Windows Excel text cells, e.g. "6/15/26")
  const mdyShort = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
  if (mdyShort) {
    const [, mm, dd, yy] = mdyShort;
    const yyyy = parseInt(yy, 10) >= 50 ? `19${yy}` : `20${yy}`;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  return null;
}

// ─── Lead status normalizer ───────────────────────────────────────────────────
const _LEAD_STATUS_MAP = {
  nuevo:              'nuevo',
  new:                'nuevo',
  contactado:         'contactado',
  contacted:          'contactado',
  en_negociacion:     'en_negociacion',
  'en negociacion':   'en_negociacion',
  negociacion:        'en_negociacion',
  negociacion_:       'en_negociacion',
  ennegociacion:      'en_negociacion',
  negociando:         'en_negociacion',
  pendiente_de_pago:  'pendiente_de_pago',
  'pendiente de pago':'pendiente_de_pago',
  pendiente:          'pendiente_de_pago',
  ganado:             'ganado',
  won:                'ganado',
  cerrado:            'ganado',
  perdido:            'perdido',
  lost:               'perdido',
  descartado:         'perdido',
};
function normalizeLeadStatus(val) {
  if (!val) return null;
  const key = String(val).trim().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[\s_]+/g, ' ')
    .trim();
  return _LEAD_STATUS_MAP[key] || _LEAD_STATUS_MAP[key.replace(/\s/g, '_')] || null;
}

// ─── Numeric parsers seguros ─────────────────────────────────────────────────
// Limpia "$", ",", espacios antes de parseFloat. Si falla → null.
function parseMoneyToNumber(val) {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return isNaN(val) ? null : val;
  const cleaned = String(val).replace(/[$\s,]/g, '').trim();
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

// Extrae el primer entero de un string como "25 años" → 25. Si falla → null.
function parsePlazoToInt(val) {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return Number.isInteger(val) ? val : Math.round(val);
  const m = String(val).match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
}

// ─── In-file duplicate detection + merge ─────────────────────────────────────
const DUP_KEY_FIELDS = {
  leads:          ['email', 'phone', 'name'],
  clients:        ['email', 'phone', 'name'],
  policies:       ['policy_number'],
  agents:         ['full_name', 'phone'],   // name+phone = same agent (renewal)
  users:          ['email'],
  inmuebles:           ['email', 'phone', 'client_name'],
  inmuebles_leads:     ['phone', 'name'],
  inmuebles_prospectos: ['phone', 'name'],
  'sales-reports': ['client_name'],
};
const MERGE_ARRAY_FIELDS = ['business_lines', 'business_types'];

function deduplicateInFile(records, module) {
  const result = [];
  let mergedCount = 0;

  if (module === 'sales-reports') {
    // Compound key: client_name + report_month. Merge business_type if different.
    for (const record of records) {
      const name  = String(record.client_name  || '').toLowerCase().trim();
      const month = String(record.report_month || '').toLowerCase().trim();
      if (!name) { result.push({ ...record }); continue; }
      const matchIdx = result.findIndex(r =>
        String(r.client_name  || '').toLowerCase().trim() === name &&
        String(r.report_month || '').toLowerCase().trim() === month
      );
      if (matchIdx >= 0) {
        // Merge business_type
        const existBt = String(result[matchIdx].business_type || '');
        const newBt   = String(record.business_type || '').trim();
        if (newBt && !existBt.split(',').map(s => s.trim()).includes(newBt)) {
          result[matchIdx].business_type = existBt ? `${existBt}, ${newBt}` : newBt;
        }
        // Merge other non-empty fields (keep first non-empty value)
        for (const k of Object.keys(record)) {
          if (['business_type', 'client_name', 'report_month'].includes(k)) continue;
          if (record[k] != null && record[k] !== '' && !result[matchIdx][k]) {
            result[matchIdx][k] = record[k];
          }
        }
        mergedCount++;
      } else {
        result.push({ ...record });
      }
    }
    return { records: result, mergedCount };
  }

  // ── Agents: deduplicate by full_name + phone (AND match = renewal) ───────
  if (module === 'agents') {
    for (const record of records) {
      const name  = String(record.full_name || '').toLowerCase().trim();
      const phone = String(record.phone || '').replace(/\D/g, '');
      const matchIdx = result.findIndex(r => {
        const rName  = String(r.full_name || '').toLowerCase().trim();
        const rPhone = String(r.phone || '').replace(/\D/g, '');
        return name && rName === name && phone && rPhone === phone;
      });
      if (matchIdx >= 0) {
        // Merge aseguradoras
        const existArr = Array.isArray(result[matchIdx].aseguradoras) ? result[matchIdx].aseguradoras : [];
        const newArr = Array.isArray(record.aseguradoras) ? record.aseguradoras : [];
        result[matchIdx].aseguradoras = [...new Set([...existArr, ...newArr])];
        // Keep the newest hire_date
        const existDate = result[matchIdx].hire_date || '';
        const newDate = record.hire_date || '';
        if (newDate && (!existDate || newDate > existDate)) {
          result[matchIdx].hire_date = newDate;
        }
        // Fill in other missing fields from new record
        for (const k of ['email', 'agent_key', 'address', 'birth_date', 'section']) {
          if (record[k] && !result[matchIdx][k]) result[matchIdx][k] = record[k];
        }
        mergedCount++;
      } else {
        result.push({ ...record });
      }
    }
    return { records: result, mergedCount };
  }

  // ── Policies: AND logic — duplicado solo si policy_number + emisor + año coinciden ─
  if (module === 'policies') {
    for (const record of records) {
      const pnum   = String(record.policy_number || '').trim();
      const emisor = String(record['custom:emisor'] || '').toLowerCase().trim();
      const year   = String(record.start_date   || '').trim().slice(0, 4);
      if (!pnum) { result.push({ ...record }); continue; }
      const matchIdx = result.findIndex(r => {
        const rPnum   = String(r.policy_number || '').trim();
        const rEmisor = String(r['custom:emisor'] || '').toLowerCase().trim();
        const rYear   = String(r.start_date   || '').trim().slice(0, 4);
        return rPnum === pnum && rEmisor === emisor && rYear === year;
      });
      if (matchIdx >= 0) {
        for (const k of Object.keys(record)) {
          if (k === 'policy_number') continue;
          if (record[k] != null && record[k] !== '' && !result[matchIdx][k]) {
            result[matchIdx][k] = record[k];
          }
        }
        mergedCount++;
      } else {
        result.push({ ...record });
      }
    }
    return { records: result, mergedCount };
  }

  // ── Generic logic for other modules ──────────────────────────────────────
  const keyFields = DUP_KEY_FIELDS[module] || ['email', 'name'];
  for (const record of records) {
    let matchIdx = -1;
    for (const kf of keyFields) {
      const val = String(record[kf] || '').toLowerCase().trim();
      if (!val) continue;
      matchIdx = result.findIndex(r => {
        const rv = String(r[kf] || '').toLowerCase().trim();
        return rv && rv === val;
      });
      if (matchIdx >= 0) break;
    }
    if (matchIdx >= 0) {
      for (const mf of MERGE_ARRAY_FIELDS) {
        if (record[mf]) {
          const existArr = Array.isArray(result[matchIdx][mf]) ? result[matchIdx][mf] : (result[matchIdx][mf] ? [result[matchIdx][mf]] : []);
          const newArr = Array.isArray(record[mf]) ? record[mf] : [record[mf]];
          result[matchIdx][mf] = [...new Set([...existArr, ...newArr])];
        }
      }
      mergedCount++;
    } else {
      result.push({ ...record });
    }
  }
  return { records: result, mergedCount };
}

// ─── Sales-report special parsing ────────────────────────────────────────────
const SALES_LEGEND_TEXTS = [
  'PROSPECTO CON FECHA PARA CIERRE',
  'NEGOCIO CERRADO DOCTOS INCOMPLETOS',
  'NEGOCIO EN TRAMITE',
  'NEGOCIO EN TRÁMITE',
  'POLIZA EMITIDA Y PAGADA',
  'PÓLIZA EMITIDA Y PAGADA',
  'NEGOCIO RECHAZADO POR ALLIANZ',
];

function isSalesLegendRow(row) {
  if (!row || row.length === 0) return false;
  const text = String(row[0] || '').toUpperCase().trim();
  return SALES_LEGEND_TEXTS.some(l => text === l || text.includes(l));
}

// Parse a single sheet with the special Reporte de Ventas format:
//   Rows 1-6: metadata (skip). Row 7: real headers. Row 8+: data.
//   Advisor name lives in cell B6 (index [5][1]).
function parseSalesReportSheet(ws, sheetName) {
  const allRows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });

  // Extract advisor from B6 (0-indexed: row 5, col 1)
  let advisorName = '';
  if (allRows[5] && allRows[5][1]) {
    advisorName = String(allRows[5][1]).trim();
  }

  // Find header row containing "NOMBRE DEL PROSPECTO O CLIENTE"
  let headerRowIdx = -1;
  for (let i = 0; i < Math.min(allRows.length, 15); i++) {
    if ((allRows[i] || []).some(cell =>
      String(cell || '').toUpperCase().includes('NOMBRE DEL PROSPECTO')
    )) {
      headerRowIdx = i;
      break;
    }
  }

  if (headerRowIdx === -1 || headerRowIdx + 1 >= allRows.length) {
    return { headers: [], dataRows: [], advisorName, reportMonth: sheetName };
  }

  // Build header list (only non-empty cells)
  const rawHeaderRow = allRows[headerRowIdx];
  const headerCount = rawHeaderRow.filter(h => h !== null && h !== undefined && h !== '').length;
  const headers = rawHeaderRow.slice(0, headerCount).map(h => String(h || '').trim());

  // Data rows: skip empty, skip legend, skip rows where first cell is empty
  const dataRows = allRows.slice(headerRowIdx + 1).filter(row => {
    if (!row || row.every(c => c === null || c === undefined || c === '')) return false;
    if (!row[0] || String(row[0]).trim() === '') return false;
    if (isSalesLegendRow(row)) return false;
    return true;
  }).map(row => row.slice(0, headerCount));

  return { headers, dataRows, advisorName, reportMonth: sheetName };
}

// ─── Business line value normalizer ─────────────────────────────────────────
// Maps human-readable insurer names → stored IDs (allianz, qualitas, insignia, flipping)
const BUSINESS_LINE_MAP = {
  'allianz': 'allianz',
  'qualitas': 'qualitas',
  'qualitas seguros': 'qualitas',
  'insignia': 'insignia',
  'insignia life': 'insignia',
  'flipping': 'flipping',
  'flipping inmobiliario': 'flipping',
  'bienes raíces': 'flipping',
  'bienes raices': 'flipping',
};

function normalizeBusinessLineValue(raw) {
  const key = normalize(raw);
  return BUSINESS_LINE_MAP[key] || raw.trim();
}

// ─── Product type code normalizer ────────────────────────────────────────────
const PRODUCT_CODE_MAP = {
  'plu3':  'OptiMaxx Plus',
  'plu':   'OptiMaxx Plus',
  'plus':  'OptiMaxx Plus',
  'prot':  'OptiMaxx Protección',
  'pat':   'OptiMaxx Patrimonial',
  'eli':   'OptiMaxx Elite',
  'elite': 'OptiMaxx Elite',
  'vpl':   'Vida Plus',
  'vpl3':  'Vida Plus',
  'ato':   'Auto',
  'auto':  'Auto',
};

function normalizeProductType(val) {
  if (!val) return val;
  const v = String(val).trim().toLowerCase();
  if (PRODUCT_CODE_MAP[v]) return PRODUCT_CODE_MAP[v];
  if (v.includes('plus') && v.includes('art')) return 'OptiMaxx Plus';
  if (v.includes('protec')) return 'OptiMaxx Protección';
  if (v.includes('patrimonial')) return 'OptiMaxx Patrimonial';
  if (v.includes('elite')) return 'OptiMaxx Elite';
  if (v.includes('vida plus')) return 'Vida Plus';
  return val.trim();
}

// ─── Field definitions per module ────────────────────────────────────────────
const FIELD_VARIANTS = {
  leads: {
    name:                ['nombre', 'cliente', 'prospecto', 'lead', 'nombre del cliente', 'nombre completo'],
    email:               ['email', 'correo', 'correo electronico', 'e mail', 'mail'],
    phone:               ['telefono', 'celular', 'whatsapp', 'tel', 'movil', 'numero telefono'],
    contact_date:        ['fecha contacto', 'fecha de contacto', 'fecha primer contacto'],
    birth_date:          ['fecha nacimiento', 'nacimiento', 'fecha de nacimiento', 'cumpleanos', 'fecha nac'],
    source:              ['fuente', 'origen', 'source', 'canal'],
    business_lines:      ['linea', 'linea de negocio', 'aseguradora', 'compania', 'lineas', 'empresa', 'insurance'],
    business_types:      ['negocio', 'plan', 'producto', 'tipo negocio', 'negocio plan'],
    contributions:       ['aportaciones', 'monto', 'prima', 'amount', 'importe', 'aportacion'],
    periodicidad:        ['periodicidad', 'frecuencia', 'periodo'],
    plazo:               ['plazo', 'anos', 'duracion', 'plazo anos'],
    assigned_agent_name:  ['agente', 'asesor', 'vendedor', 'ejecutivo', 'agente asignado'],
    status:               ['estado', 'status', 'estatus'],
    notes:                ['notas', 'comentarios', 'observaciones', 'nota'],
    next_follow_up_date:  ['proximo contacto', 'prox contacto', 'siguiente contacto',
                           'fecha seguimiento', 'proximo contacto lead', 'proximo contacto (lead)'],
  },
  clients: {
    name:                ['nombre', 'cliente', 'nombre del cliente', 'nombre completo'],
    email:               ['email', 'correo', 'correo electronico'],
    phone:               ['telefono', 'celular', 'whatsapp', 'tel'],
    birth_date:          ['fecha nacimiento', 'nacimiento', 'fecha de nacimiento'],
    business_lines:      ['linea', 'linea de negocio', 'aseguradora', 'compania', 'empresa', 'insurance'],
    business_types:      ['negocio', 'plan', 'producto'],
    contributions:       ['aportaciones', 'monto', 'prima', 'amount'],
    status:              ['estado', 'status', 'estatus'],
    assigned_agent_name: ['agente', 'asesor', 'vendedor'],
    notes:               ['notas', 'comentarios', 'observaciones'],
  },
  policies: {
    policy_number:   ['poliza', 'numero poliza', 'numero de poliza', 'no poliza', 'n poliza', 'folio poliza', 'numero_poliza', 'no. poliza', 'no poliza'],
    client_name:     ['cliente', 'nombre cliente', 'asegurado', 'nombre del cliente', 'contratante', 'nombre contratante', 'titular', 'nombre titular'],
    agent_name:      ['agente', 'asesor', 'vendedor', 'promotor',
                      'nombre del agente', 'nombre agente', 'agente nombre',
                      'asesor nombre', 'nombre del asesor'],
    clave_agente:    ['clave agente', 'clave del agente', 'clave de agente',
                      'clave asesor', 'clave del asesor', 'clave de asesor',
                      'clave', 'clave_agente', 'claveagente',
                      'no agente', 'no de agente', 'no del agente',
                      'num agente', 'numero agente', 'numero del agente', 'id agente'],
    business_line:   ['aseguradora', 'compania', 'company', 'linea', 'empresa', 'insurance'],
    product_type:    ['producto', 'plan', 'tipo', 'tipo producto', 'tipo de plan', 'plan contratado'],
    premium:         ['prima total', 'prima', 'amount', 'importe'],
    contributions:   ['aportacion', 'aportaciones', 'monto aportaciones', 'contributions'],
    plazo:           ['plazo', 'anos poliza', 'plazo poliza', 'anos contratados', 'duracion poliza', 'anos', 'antigüedad', 'antiguedad', 'años'],
    start_date:      ['vigencia inicio', 'inicio vigencia', 'fecha inicio', 'inicio', 'fecha de inicio', 'inicio de vigencia'],
    end_date:        ['vigencia fin', 'fin vigencia', 'fecha vencimiento', 'vencimiento', 'fecha fin', 'fin de vigencia'],
    status:          ['estado', 'status', 'estatus', 'estatus de la poliza', 'estado de la poliza', 'estatus poliza', 'estado poliza'],
    notes:           ['notas', 'comentarios', 'observaciones'],
    dias_retraso:    ['dias retraso', 'dias de retraso', 'dias de atraso', 'dias mora', 'mora'],
    'custom:emisor':                    ['emisor', 'codigo emisor', 'codigo producto'],
    'custom:periodicidad':              ['periodicidad', 'frecuencia', 'frecuencia pago', 'frecuencia de pago'],
    'custom:serie_recibo':              ['serie recibo', 'serie', 'serie de recibo', 'recibo serie'],
    'custom:no_solicitud':              ['no solicitud', 'no. solicitud', 'numero solicitud', 'solicitud'],
    'custom:plazo_comprometido':        ['plazo comprometido', 'plazo de pago'],
    'custom:monto_aportaciones':        ['monto de aportaciones', 'monto aportaciones'],
    'custom:aportaciones_esperadas':    ['aportaciones esperadas', 'num aportaciones esperadas'],
    'custom:monto_aportaciones_esp':    ['monto aportaciones esperadas', 'monto esperado'],
    'custom:conducto_cobro':            ['conducto cobro', 'conducto de cobro', 'forma pago', 'metodo pago'],
    'custom:respuesta_banco':           ['respuesta banco', 'respuesta del banco'],
    'custom:ultima_fecha_pago':         ['ultima fecha pago', 'ultima fecha de pago', 'ultimo pago'],
    'custom:monto_proximo_pago':        ['monto proximo pago', 'proximo pago', 'siguiente pago'],
    'custom:fecha_recibo_pendiente':    ['fecha de recibo pendiente de cobro', 'fecha recibo pendiente de cobro', 'fecha recibo pendiente', 'recibo pendiente', 'fecha recibo'],
    'custom:vin':                       ['vin', 'numero vin', 'no vin'],
    'custom:prima_neta_usd':            ['prima neta usd', 'prima neta', 'prima neta mxn', 'prima usd', 'prima dolares'],
    'custom:valor_plan_original':       ['valor plan original', 'valor plan', 'valor original'],
    'custom:moneda':                    ['moneda', 'divisa', 'currency'],
    'custom:periodo':                   ['periodo', 'period'],
    'custom:aportaciones_comp_pagadas': ['aportaciones comprometidas pagadas', 'comp pagadas', 'aportaciones comp'],
    'custom:aportaciones_ini_pagadas':  ['aportaciones iniciales pagadas', 'ini pagadas', 'aportaciones ini'],
    'custom:dias_atraso_import':        ['dias atraso importado', 'atraso import', 'dias de atraso (importado)'],
  },
  agents: {
    full_name:  ['nombre', 'agente', 'nombre del agente', 'nombre completo', 'nombre agente'],
    email:      ['email', 'correo', 'correo electronico'],
    phone:      ['telefono', 'celular', 'tel', 'movil'],
    agent_key:  ['clave', 'key', 'codigo', 'id agente', 'clave agente', 'no agente', 'numero agente'],
    aseguradoras: ['aseguradora', 'aseguradoras', 'compania', 'empresa', 'linea'],
    address:    ['direccion', 'domicilio', 'calle'],
    birth_date: ['fecha nacimiento', 'nacimiento', 'fecha de nacimiento'],
    hire_date:          ['fecha alta allianz', 'fecha alta', 'alta', 'fecha de alta', 'ingreso', 'fecha ingreso'],
    hire_date_insignia: ['fecha alta insignia', 'alta insignia', 'fecha de alta insignia', 'ingreso insignia'],
    hire_date_qualitas: ['fecha alta qualitas', 'fecha alta quálitas', 'alta qualitas', 'fecha de alta qualitas', 'ingreso qualitas'],
    notes:      ['notas', 'comentarios'],
  },
  users: {
    full_name: ['nombre', 'agente', 'nombre del agente', 'nombre completo', 'usuario'],
    email:     ['email', 'correo', 'correo electronico'],
    phone:     ['telefono', 'celular', 'tel'],
    role:      ['rol', 'role', 'tipo', 'perfil'],
  },
  inmuebles: {
    client_name:  ['nombre', 'cliente', 'nombre del cliente', 'propietario'],
    contact_date: ['fecha', 'fecha registro'],
    phone:        ['telefono', 'celular', 'tel'],
    email:        ['email', 'correo'],
    has_debt:     ['adeudo', 'tiene adeudo', 'deuda'],
    status:       ['estado', 'status', 'estatus'],
    infonavit:    ['infonavit', 'credito infonavit'],
    location:     ['colonia', 'ubicacion', 'direccion', 'zona'],
    notes:        ['notas', 'comentarios'],
  },
  inmuebles_leads: {
    contact_id: ['id de contacto', 'id contacto', 'id'],
    name:       ['nombre'],
    date:       ['fecha'],
    has_debt:   ['tiene adeudo', 'adeudo', 'deuda'],
    occupancy:  ['esta habitada rentada o desocupada', 'habitada rentada o desocupada', 'estado propiedad', 'ocupacion propiedad', 'ocupacion'],
    infonavit:  ['la casa es con credito infonavit', 'credito infonavit', 'infonavit'],
    colonia:    ['en que colonia se encuentra', 'colonia', 'ubicacion'],
    phone:      ['numero de contacto', 'telefono', 'celular', 'tel', 'numero'],
    status:     ['estatus', 'semaforo', 'estado semaforo'],
  },
  inmuebles_prospectos: {
    num:              ['no', 'numero', 'num', 'n'],
    date:             ['fecha'],
    name:             ['nombre'],
    phone:            ['telefono', 'celular', 'tel', 'numero'],
    estado:           ['estado'],
    municipio:        ['municipio'],
    colonia:          ['colonia'],
    comment:          ['comentario', 'comentarios', 'notas', 'observaciones'],
    water:            ['agua'],
    light:            ['luz'],
    predial:          ['predial'],
    credit:           ['credito', 'credito infonavit'],
    status:           ['estatus'],
    rejection_reason: ['razon rechazo', 'razon de rechazo', 'motivo rechazo', 'motivo'],
  },
  'sales-reports': {
    client_name:       ['nombre', 'cliente', 'nombre del cliente', 'nombre del prospecto o cliente', 'prospecto', 'nombre prospecto'],
    occupation:        ['ocupacion', 'trabajo', 'profesion', 'actividad'],
    business_type:     ['negocio', 'plan', 'producto', 'tipo'],
    advisor_name:      ['asesor', 'agente', 'vendedor'],
    amount:            ['monto', 'amount', 'prima', 'aportacion', 'importe'],
    periodicity:       ['periodicidad', 'frecuencia', 'periodo'],
    term:              ['plazo', 'anos', 'duracion', 'term'],
    next_contact_date: ['proximo contacto', 'siguiente contacto', 'proxima fecha', 'fecha seguimiento', 'prox contacto', 'fecha prox contacto'],
    status:            ['estado', 'status', 'estatus'],
    presentation:      ['presentacion', 'presento', 'presentado'],
    comments:          ['comentarios', 'notas', 'observaciones', 'comentarios actualizados a la fecha'],
    report_month:      ['mes reporte', 'mes', 'periodo reporte', 'hoja'],
  },
};

const FIELD_LABELS = {
  leads: {
    name: 'Nombre', email: 'Email', phone: 'Teléfono',
    contact_date: 'Fecha de Contacto', birth_date: 'Fecha Nacimiento',
    source: 'Fuente', business_lines: 'Aseguradora',
    business_types: 'Negocio/Plan', contributions: 'Aportaciones',
    periodicidad: 'Periodicidad', plazo: 'Plazo',
    assigned_agent_name: 'Agente', status: 'Estado', notes: 'Notas',
    next_follow_up_date: 'Próximo Contacto',
  },
  clients: {
    name: 'Nombre', email: 'Email', phone: 'Teléfono',
    birth_date: 'Fecha Nacimiento', business_lines: 'Aseguradora',
    business_types: 'Negocio/Plan', contributions: 'Aportaciones',
    status: 'Estado', assigned_agent_name: 'Agente', notes: 'Notas',
  },
  policies: {
    // Campos nativos
    policy_number: 'Número Póliza',
    client_name:   'Cliente',
    agent_name:    'Agente',
    business_line: 'Aseguradora',
    product_type:  'Producto/Plan',
    premium:       'Prima Total',
    contributions: 'Aportaciones',
    start_date:    'Vigencia Inicio',
    end_date:      'Vigencia Fin',
    status:        'Estado',
    notes:         'Notas',
    dias_retraso:  'Días de Atraso',
    clave_agente:  'Clave Agente',
    // Campos personalizados
    'custom:emisor':                    'Emisor (código)',
    'custom:periodicidad':              'Periodicidad',
    'custom:serie_recibo':              'Serie Recibo',
    'custom:no_solicitud':              'No. Solicitud',
    'custom:plazo_comprometido':        'Plazo Comprometido',
    'custom:monto_aportaciones':        'Monto de Aportaciones',
    'custom:aportaciones_esperadas':    'Aportaciones Esperadas',
    'custom:monto_aportaciones_esp':    'Monto Aportaciones Esperadas',
    'custom:conducto_cobro':            'Conducto Cobro',
    'custom:respuesta_banco':           'Respuesta Banco',
    'custom:ultima_fecha_pago':         'Última Fecha de Pago',
    'custom:monto_proximo_pago':        'Monto Próximo Pago',
    'custom:fecha_recibo_pendiente':    'Fecha Recibo Pendiente de Cobro',
    'custom:vin':                       'VIN',
    'custom:prima_neta_usd':            'Prima Neta',
    'custom:valor_plan_original':       'Valor Plan Original',
    'custom:moneda':                    'Moneda',
    'custom:periodo':                   'Periodo',
    'custom:aportaciones_comp_pagadas': 'Aportaciones Comprometidas Pagadas',
    'custom:aportaciones_ini_pagadas':  'Aportaciones Iniciales Pagadas',
    'custom:dias_atraso_import':        'Días de Atraso (importado)',
  },
  agents: {
    full_name: 'Nombre', email: 'Email', phone: 'Teléfono',
    agent_key: 'Clave', aseguradoras: 'Aseguradora(s)',
    address: 'Dirección', birth_date: 'Fecha Nacimiento',
    hire_date: 'Fecha de Alta Allianz', hire_date_insignia: 'Fecha de Alta Insignia', hire_date_qualitas: 'Fecha de Alta Quálitas', section: 'Sección', notes: 'Notas',
  },
  users:  { full_name: 'Nombre', email: 'Email', phone: 'Teléfono', role: 'Rol' },
  inmuebles: {
    client_name: 'Nombre Cliente', contact_date: 'Fecha', phone: 'Teléfono',
    email: 'Email', has_debt: '¿Tiene Adeudo?', status: 'Estado',
    infonavit: 'Crédito Infonavit', location: 'Colonia/Ubicación', notes: 'Notas',
  },
  inmuebles_leads: {
    contact_id: 'ID Contacto', name: 'Nombre', date: 'Fecha',
    has_debt: '¿Tiene adeudo?', occupancy: 'Estado propiedad',
    infonavit: '¿Crédito Infonavit?', colonia: 'Colonia',
    phone: 'Número de contacto', status: 'Estatus',
  },
  inmuebles_prospectos: {
    num: 'No.', date: 'Fecha', name: 'Nombre', phone: 'Teléfono',
    estado: 'Estado', municipio: 'Municipio', colonia: 'Colonia',
    comment: 'Comentario', water: 'Agua', light: 'Luz',
    predial: 'Predial', credit: 'Crédito', status: 'Estatus',
    rejection_reason: 'Razón de rechazo',
  },
  'sales-reports': {
    client_name: 'Nombre Cliente', occupation: 'Ocupación', business_type: 'Negocio/Plan',
    advisor_name: 'Asesor', amount: 'Monto', periodicity: 'Periodicidad',
    term: 'Plazo', next_contact_date: 'Próximo Contacto',
    status: 'Estado', presentation: 'Presentación', comments: 'Comentarios',
    report_month: 'Mes Reporte',
  },
};

// Export column config
const EXPORT_CONFIG = {
  leads: {
    cols: ['name', 'email', 'phone', 'contact_date', 'birth_date', 'source', 'business_lines', 'business_types', 'contributions', 'periodicidad', 'plazo', 'assigned_agent_name', 'status', 'notes'],
    headers: ['Nombre', 'Email', 'Teléfono', 'Fecha Contacto', 'Fecha Nacimiento', 'Fuente', 'Línea Negocio', 'Plan/Producto', 'Aportaciones', 'Periodicidad', 'Plazo', 'Agente Asignado', 'Estado', 'Notas'],
  },
  clients: {
    cols: ['name', 'email', 'phone', 'birth_date', 'business_lines', 'business_types', 'contributions', 'periodicidad', 'status', 'assigned_agent_name', 'notes'],
    headers: ['Nombre', 'Email', 'Teléfono', 'Fecha Nacimiento', 'Línea Negocio', 'Plan/Producto', 'Aportaciones', 'Periodicidad', 'Estado', 'Agente Asignado', 'Notas'],
  },
  policies: {
    cols: [
      'policy_number','client_name','agent_name','business_line','product_type',
      'status','emisor','start_date','end_date',
      'premium','prima_anual_reportada','periodicidad','serie_recibo',
      'plazo_comprometido','contributions','aportaciones_esperadas',
      'valor_plan_original','fecha_recibo_pendiente','dias_retraso',
      'plan_contratado','conducto_cobro','respuesta_banco',
      'sede','clave_agente','conv_points',
    ],
    headers: [
      'Póliza','Cliente','Agente','Aseguradora','Producto',
      'Estado','Emisor','Inicio Vigencia','Fin Vigencia',
      'Prima Neta','Prima Total','Periodicidad','Serie Recibo',
      'Plazo Comprometido','Aportaciones Pagadas','Aportaciones Esperadas',
      'Valor Plan Original','Fecha Recibo Pendiente','Días Atraso',
      'Plan Contratado','Conducto Cobro','Respuesta Banco',
      'Sede','Clave Agente','Puntos Conv.',
    ],
  },
  agents: {
    cols: ['full_name', 'email', 'phone', 'agent_key', 'aseguradoras', 'address', 'birth_date', 'hire_date', 'hire_date_insignia', 'hire_date_qualitas', 'section'],
    headers: ['Nombre', 'Email', 'Teléfono', 'Clave', 'Aseguradora(s)', 'Dirección', 'Fecha Nacimiento', 'Fecha de Alta Allianz', 'Fecha de Alta Insignia', 'Fecha de Alta Quálitas', 'Sección'],
  },
  users: {
    cols: ['full_name', 'email', 'role', 'is_active'],
    headers: ['Nombre', 'Email', 'Rol', 'Activo'],
  },
  'sales-reports': {
    cols: ['client_name', 'occupation', 'business_type', 'advisor_name', 'amount', 'periodicity', 'next_contact_date', 'presentation', 'comments'],
    headers: ['Cliente', 'Ocupación', 'Negocio/Plan', 'Asesor', 'Monto', 'Periodicidad', 'Próximo Contacto', 'Presentación', 'Comentarios'],
  },
  inmuebles: {
    cols: ['client_name', 'phone', 'email', 'has_debt', 'occupancy_status', 'has_infonavit', 'colonia', 'notes'],
    headers: ['Nombre Cliente', 'Teléfono', 'Email', '¿Tiene Adeudo?', 'Estado Ocupación', 'Crédito Infonavit', 'Colonia', 'Notas'],
  },
};

// ─── Auto-detect column mapping ──────────────────────────────────────────────
function autoDetectMapping(rawHeaders, module, policyCustomFields = []) {
  const variants = FIELD_VARIANTS[module] || {};
  const map = {};
  const detected = new Set();

  rawHeaders.forEach(header => {
    const norm = normalize(header);
    let matched = null;

    // Paso 1: FIELD_VARIANTS hardcodeados
    for (const [fieldKey, variantList] of Object.entries(variants)) {
      if (variantList.includes(norm)) {
        matched = fieldKey;
        break;
      }
    }

    // Paso 2: campos custom dinámicos por slug o label
    if (!matched && module === 'policies') {
      for (const cf of policyCustomFields) {
        if (normalize(cf.name) === norm || normalize(cf.label) === norm) {
          matched = `custom:${cf.name}`;
          break;
        }
      }
    }

    map[header] = matched;
    if (matched) detected.add(header);
  });

  return { map, detected };
}

// ─── Apply mapping to raw rows ────────────────────────────────────────────────
const ARRAY_FIELDS  = new Set(['business_types', 'business_lines', 'aseguradoras']);
const NUMBER_FIELDS = new Set(['contributions', 'premium', 'amount', 'plazo', 'dias_retraso']);
const DATE_FIELDS   = new Set(['birth_date', 'hire_date', 'hire_date_insignia', 'hire_date_qualitas', 'contact_date', 'next_contact_date', 'next_follow_up_date', 'start_date', 'end_date']);

function applyMapping(rawRows, rawHeaders, columnMap) {
  return rawRows
    .filter(row => !row.every(cell => cell === undefined || cell === null || cell === ''))
    .map(row => {
      const record = {};
      rawHeaders.forEach((header, colIdx) => {
        const fieldKey = columnMap[header];
        if (!fieldKey) return;
        let val = row[colIdx];
        if (val === undefined || val === null || val === '') return;

        if (ARRAY_FIELDS.has(fieldKey) && typeof val === 'string') {
          val = val.split(',').map(s => normalizeBusinessLineValue(s)).filter(Boolean);
        } else if (fieldKey === 'business_line') {
          val = normalizeBusinessLineValue(String(val));
        } else if (fieldKey === 'product_type') {
          val = normalizeProductType(String(val));
        } else if (fieldKey.startsWith('custom:')) {
          // Campos personalizados: siempre string, sin coerción de tipo
          val = String(val).trim();
        } else if (DATE_FIELDS.has(fieldKey)) {
          val = formatExcelDate(val);
          if (val === null) return; // formato no reconocido → no guardar
        } else if (fieldKey === 'status') {
          val = normalizeLeadStatus(val) || String(val).trim();
        } else if (fieldKey === 'plazo') {
          val = parsePlazoToInt(val);
          if (val === null) return;
        } else if (NUMBER_FIELDS.has(fieldKey)) {
          val = parseMoneyToNumber(val);
          if (val === null) return;
        } else {
          val = String(val).trim();
        }
        record[fieldKey] = val;
      });
      // Derivar emisor desde nombre de producto para pólizas de Inversión (OPPT / SVIP)
      // Corre antes de la inferencia de aseguradora para que ésta pueda aprovecharlo
      if (!record['custom:emisor'] && record['product_type']) {
        const ptNorm = String(record['product_type']).toLowerCase()
          .normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
        if (ptNorm.includes('patrimonial')) {
          record['custom:emisor'] = 'OPPT';
        } else if (ptNorm.includes('elite')) {
          record['custom:emisor'] = 'SVIP';
        }
      }
      // Inferir aseguradora desde código de emisor si business_line no fue mapeado
      if (!record['business_line'] && record['custom:emisor']) {
        const EMISOR_TO_ASEGURADORA = {
          'plu3': 'allianz', 'plu': 'allianz', 'prot': 'allianz',
          'plus': 'allianz', 'svip': 'allianz', 'oppt': 'allianz',
          'vpl':  'insignia', 'vpl3': 'insignia',
          'ato':  'qualitas', 'ato3': 'qualitas', 'auto': 'qualitas',
        };
        const codigo = String(record['custom:emisor']).toLowerCase().trim();
        const infer = EMISOR_TO_ASEGURADORA[codigo];
        if (infer) record['business_line'] = infer;
      }
      // Auto-fill contributions desde valor_plan_original para productos Ahorro Allianz
      // cuando contributions = 0 (el Valor Plan es la base real para puntos de convención)
      const AHORRO_PRODUCT_TYPES = new Set([
        'OptiMaxx Plus', 'O. Plus', 'O.Plus', 'Ahorro Educación', 'Ahorro',
        'OptiMaxx Protección', 'OptiMaxx Patrimonial', 'OptiMaxx Elite',
      ]);
      const bl = (record['business_line'] || '').toLowerCase();
      const pt = record['product_type'] || '';
      const contribVal = record['contributions'];
      if (bl === 'allianz' && AHORRO_PRODUCT_TYPES.has(pt) &&
          (contribVal === 0 || contribVal === null || contribVal === undefined)) {
        const vpo = parseMoneyToNumber(record['custom:valor_plan_original']);
        if (vpo && vpo > 0) {
          record['contributions'] = vpo;
        }
      }
      return record;
    })
    // Ignorar filas de encabezado repetido dentro del Excel (ej. Producto === 'Producto')
    .filter(r => r.product_type !== 'Producto')
    .filter(r => Object.keys(r).length > 0);
}

// ─── String similarity (Dice bigram) for fuzzy field matching ────────────────
function stringSimilarity(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const bigrams = (s) => {
    const set = new Map();
    for (let i = 0; i < s.length - 1; i++) {
      const bg = s.slice(i, i + 2);
      set.set(bg, (set.get(bg) || 0) + 1);
    }
    return set;
  };
  const aMap = bigrams(a);
  const bMap = bigrams(b);
  let intersection = 0;
  for (const [bg, count] of aMap) {
    intersection += Math.min(count, bMap.get(bg) || 0);
  }
  return (2 * intersection) / (a.length - 1 + b.length - 1);
}

// ─── Module → API path ────────────────────────────────────────────────────────
function moduleToPath(module) {
  const map = { 'sales-reports': 'sales-reports' };
  return map[module] || module;
}

// ─── Validate that a string looks like a person's name (not metadata) ─────────
function isValidPersonName(str) {
  if (!str || str.trim() === '') return false;
  // Contains digits, colons, or starts with META → it's metadata
  if (/\d/.test(str) || str.includes(':') || str.toUpperCase().startsWith('META')) return false;
  return true;
}

// ─── Categorías seleccionables del modal "Importación completada" (solo pólizas) ──
const RESULT_CATEGORIES = [
  {
    key: 'imported', label: 'Importados', listKey: 'new_policies',
    base: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-400', ring: 'ring-green-500',
  },
  {
    key: 'updated', label: 'Actualizados', listKey: 'updated_policies',
    base: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-400', ring: 'ring-blue-500',
  },
  {
    key: 'renewed', label: 'Renovadas', listKey: 'renewed_policies',
    base: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    text: 'text-purple-700 dark:text-purple-400', ring: 'ring-purple-500',
  },
  {
    key: 'omitted', label: 'Omitidas', listKey: null,
    base: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    text: 'text-amber-700 dark:text-amber-400', ring: 'ring-amber-500',
  },
  {
    key: 'skipped', label: 'Ignorados', listKey: null,
    base: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
    text: 'text-gray-700 dark:text-gray-300', ring: 'ring-gray-500',
  },
];
// 'omitted' NO lee de importResult — solo cuenta lo que el usuario marcó explícitamente con el botón
// "Marcar como omitidas" en el preview (ver markedOmittedList), nunca el diff automático del import.
const RESULT_COUNT_KEYS = { imported: 'imported', updated: 'updated', renewed: 'pending_renewals', skipped: 'skipped' };

// ─── Main component ──────────────────────────────────────────────────────────
export const ImportExportManager = ({
  module,
  apiUrl,
  data,
  onSuccess,
  onImport,    // legacy fallback
  onExport,
  fetchAllForExport,  // async () => allRecords[] — para exportar sin límite de paginación
  exportAsCsv = false,
  disabled = false,
  currentUserName = '',  // fallback for sales-report advisor
  policyCustomFields = [],
}) => {
  const fileInputRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // step: 'sheet-select' (sales-reports only) | 'mapping' | 'preview' | 'result'
  const [step, setStep] = useState('mapping');

  // Sales-report sheet selection state
  const [rawWorkbook, setRawWorkbook]       = useState(null);
  const [sheetNames, setSheetNames]         = useState([]);
  const [selectedSheetName, setSelectedSheetName] = useState('');
  const [importMode, setImportMode]         = useState('single'); // 'single' | 'all'

  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows]       = useState([]);
  const [columnMap, setColumnMap]   = useState({});
  const [autoDetected, setAutoDetected] = useState(new Set());

  // H: opciones de campo dinámicas desde backend (null = no cargado, {}= fallback)
  const [dynamicFieldOptions, setDynamicFieldOptions] = useState(null);
  const [savedMappings, setSavedMappings]             = useState({});
  const [fuzzyMatched, setFuzzyMatched]               = useState(new Set());

  const [mappedRecords, setMappedRecords] = useState([]);
  const [inFileMergedCount, setInFileMergedCount] = useState(0);
  const [checkResult, setCheckResult]     = useState(null);
  const [duplicateAction, setDuplicateAction] = useState('skip');
  const [importResult, setImportResult]   = useState(null);
  const [resultCategory, setResultCategory] = useState('imported');
  const [loading, setLoading] = useState(false);
  const [markingOmitted, setMarkingOmitted] = useState(false);
  const [omittedMarked, setOmittedMarked] = useState(false);
  const [markedOmittedList, setMarkedOmittedList] = useState([]);
  const [manualMonth, setManualMonth] = useState('');
  // Cross-month follow-up decisions (PARTE B)
  const [crossMonthMatches, setCrossMonthMatches] = useState([]);
  const [crossMonthDecisions, setCrossMonthDecisions] = useState({});
  // Aseguradora selector (policies only — Step 1.5)
  const [showAseguradoraSelector, setShowAseguradoraSelector] = useState(false);
  const [forcedAseguradora, setForcedAseguradora]             = useState(null);
  const [pendingColumnMap, setPendingColumnMap]               = useState(null);
  // Sede selector (agents only — Step 0.5)
  const [agentImportSede, setAgentImportSede]                 = useState(null); // null = not yet selected

  const { user } = useAuth();

  const fieldLabels = FIELD_LABELS[module] || {};
  const exportCfg   = EXPORT_CONFIG[module] || { cols: [], headers: [] };
  const apiPath     = moduleToPath(module);

  // H: cargar opciones dinámicas y mapeos guardados al abrir el modal de mapeo
  useEffect(() => {
    if (!dialogOpen || step !== 'mapping' || !['policies', 'leads'].includes(module) || !apiUrl) return;
    const aseg = module === 'leads' ? 'leads' : (forcedAseguradora || 'all');
    Promise.all([
      axios.get(`${apiUrl}/import/field-options?module=${module}`),
      axios.get(`${apiUrl}/import/mappings?aseguradora=${aseg}`),
    ])
      .then(([optRes, mapRes]) => {
        setDynamicFieldOptions(optRes.data);
        const saved = mapRes.data || {};
        setSavedMappings(saved);

        // Aplicar mapeos guardados (case-insensitive)
        setColumnMap(prev => {
          const next = { ...prev };
          const rawHeadersLower = rawHeaders.map(h => h.toLowerCase().trim());
          for (const [colExcel, fieldKey] of Object.entries(saved)) {
            const idx = rawHeadersLower.indexOf(colExcel.toLowerCase().trim());
            if (idx !== -1 && fieldKey) {
              next[rawHeaders[idx]] = fieldKey;
            }
          }
          return next;
        });

        // Fuzzy match para columnas aún sin mapeo
        const allOptions = [
          ...(optRes.data.standard || []),
          ...(optRes.data.custom || []),
        ];
        const fuzzySet = new Set();
        setColumnMap(prev => {
          const next = { ...prev };
          const savedLower = Object.fromEntries(
            Object.keys(saved).map(k => [k.toLowerCase().trim(), true])
          );
          for (const h of rawHeaders) {
            if (h.startsWith('__meta_')) continue;
            if (next[h]) continue; // ya mapeado (auto-detect o guardado)
            if (savedLower[h.toLowerCase().trim()]) continue;
            const normH = normalize(h).replace(/\s+/g, '');
            let bestScore = 0, bestKey = null;
            for (const opt of allOptions) {
              const normLabel = normalize(opt.label).replace(/\s+/g, '');
              const score = stringSimilarity(normH, normLabel);
              if (score > 0.7 && score > bestScore) {
                bestScore = score;
                bestKey = opt.key;
              }
            }
            if (bestKey) {
              next[h] = bestKey;
              fuzzySet.add(h);
            }
          }
          return next;
        });
        setFuzzyMatched(fuzzySet);
      })
      .catch(() => {
        // Fallback silencioso: se mantiene FIELD_VARIANTS + FIELD_LABELS
        setDynamicFieldOptions(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, step, module, forcedAseguradora]);

  // ── File parsing ────────────────────────────────────────────────────────────
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!['.xls', '.xlsx', '.csv'].includes(ext)) {
      toast.error('Formato no soportado. Use XLS, XLSX o CSV.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true, cellStyles: true });

        if (module === 'sales-reports') {
          // Special flow: let user pick which sheet(s) to import
          setRawWorkbook(wb);
          setSheetNames(wb.SheetNames);
          setSelectedSheetName(wb.SheetNames[0] || '');
          setImportMode('single');
          setCheckResult(null);
          setImportResult(null);
          setMarkingOmitted(false);
          setOmittedMarked(false);
          setMarkedOmittedList([]);
          setDuplicateAction('skip');
          setStep('sheet-select');
          setDialogOpen(true);
          // Pre-detect month from first sheet
          const firstWs = wb.Sheets[wb.SheetNames[0]];
          setManualMonth(detectReportMonth(firstWs, wb.SheetNames[0]));
          return;
        }

        // Agents: special 3-section parsing (ACTIVOS / NOVELES / BAJAS)
        if (module === 'agents') {
          const ws = wb.Sheets[wb.SheetNames[0]];
          const allRows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });

          // Find header row (first non-empty row)
          let headerRowIdx = 0;
          for (let i = 0; i < Math.min(allRows.length, 10); i++) {
            const cells = (allRows[i] || []).filter(c => c !== null && c !== undefined && String(c).trim() !== '');
            if (cells.length >= 2) { headerRowIdx = i; break; }
          }
          const headers = (allRows[headerRowIdx] || []).map(h => String(h || '').trim()).filter(Boolean);

          // Walk data rows, detect section separators
          const META_SECTION = '__meta_section__';
          const sectionRows = [];
          let currentSection = 'activo';
          for (let i = headerRowIdx + 1; i < allRows.length; i++) {
            const row = allRows[i] || [];
            // Check if any cell is a section separator
            const rowText = row.map(c => String(c || '').trim().toUpperCase()).filter(Boolean);
            if (rowText.some(t => t === 'NOVELES' || t === 'NOVEL')) {
              currentSection = 'novel'; continue;
            }
            if (rowText.some(t => t === 'BAJAS' || t === 'BAJA')) {
              currentSection = 'baja'; continue;
            }
            // Skip blank rows
            if (row.every(c => c === null || c === undefined || String(c).trim() === '')) continue;
            sectionRows.push([...row, currentSection]);
          }

          const extHeaders = [...headers, META_SECTION];
          const { map, detected } = autoDetectMapping(headers, 'agents');
          map[META_SECTION] = 'section';
          detected.add(META_SECTION);

          setRawHeaders(extHeaders);
          setRawRows(sectionRows);
          setColumnMap(map);
          setAutoDetected(detected);
          setAgentImportSede(null);
          setCheckResult(null);
          setImportResult(null);
          setDuplicateAction('skip');
          setStep('sede-select');
          setDialogOpen(true);
          return;
        }

        // Inmuebles leads: auto-detect "Leads" tab + read row background colors for status
        if (module === 'inmuebles_leads') {
          const leadsSheetName = wb.SheetNames.find(n =>
            n.toLowerCase().trim() === 'leads'
          ) || wb.SheetNames[0];
          const wsL = wb.Sheets[leadsSheetName];
          const allRowsL = XLSX.utils.sheet_to_json(wsL, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });

          if (allRowsL.length < 2) {
            toast.error('El archivo está vacío o no tiene datos suficientes.');
            return;
          }

          const headersL = (allRowsL[0] || []).map(h => String(h || '').trim()).filter(Boolean);
          const dataRowsL = allRowsL.slice(1).filter(row =>
            !row.every(c => c === null || c === undefined || String(c).trim() === '')
          );

          // Read row background colors to infer status (rojo=rechazado, verde=trato_cerrado, amarillo=en_proceso, blanco=no_contactado).
          // Empty string means "no color detected" — applyMapping will skip empty values so the
          // ESTATUS column (if present) acts as automatic fallback.
          const META_STATUS = '__meta_status__';
          const rowsWithStatus = dataRowsL.map((row, rowIdx) => {
            const actualRowIdx = rowIdx + 1; // offset for header
            let colorStatus = ''; // default: defer to ESTATUS column
            for (let c = 0; c < Math.min(headersL.length, 4); c++) {
              const cellRef = XLSX.utils.encode_cell({ r: actualRowIdx, c });
              const cell = wsL[cellRef];
              const rgb = cell?.s?.fill?.fgColor?.rgb || cell?.s?.fill?.bgColor?.rgb || '';
              if (rgb) {
                const mapped = mapExcelRowColorToLeadStatus(rgb);
                if (mapped !== null) { colorStatus = mapped; break; }
              }
            }
            return [...row, colorStatus];
          });

          const extHeadersL = [...headersL, META_STATUS];
          const { map: mapL, detected: detectedL } = autoDetectMapping(headersL, 'inmuebles_leads');
          mapL[META_STATUS] = 'status';
          detectedL.add(META_STATUS);

          setRawHeaders(extHeadersL);
          setRawRows(rowsWithStatus);
          setColumnMap(mapL);
          setAutoDetected(detectedL);
          setStep('mapping');
          setCheckResult(null);
          setImportResult(null);
          setDuplicateAction('skip');
          setDialogOpen(true);
          return;
        }

        // Inmuebles prospectos: auto-detect "Prospectos en Seguimiento" tab
        if (module === 'inmuebles_prospectos') {
          const prospectoSheetName = wb.SheetNames.find(n => {
            const nl = n.toLowerCase().trim();
            return nl.includes('prospecto') || nl.includes('seguimiento');
          }) || wb.SheetNames[0];
          const wsP = wb.Sheets[prospectoSheetName];
          const allRowsP = XLSX.utils.sheet_to_json(wsP, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });

          if (allRowsP.length < 2) {
            toast.error('El archivo está vacío o no tiene datos suficientes.');
            return;
          }

          const headersP = (allRowsP[0] || []).map(h => String(h || '').trim()).filter(Boolean);
          const rowsP = allRowsP.slice(1);
          const { map: mapP, detected: detectedP } = autoDetectMapping(headersP, 'inmuebles_prospectos');

          setRawHeaders(headersP);
          setRawRows(rowsP);
          setColumnMap(mapP);
          setAutoDetected(detectedP);
          setStep('mapping');
          setCheckResult(null);
          setImportResult(null);
          setDuplicateAction('skip');
          setDialogOpen(true);
          return;
        }

        // Generic flow for all other modules
        const ws = wb.Sheets[wb.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });

        if (jsonData.length < 2) {
          toast.error('El archivo está vacío o no tiene datos suficientes.');
          return;
        }

        const headers = (jsonData[0] || []).map(h => String(h || '').trim()).filter(Boolean);
        const rows = jsonData.slice(1);

        const { map, detected } = autoDetectMapping(
          headers, module,
          module === 'policies' ? policyCustomFields : []
        );

        setRawHeaders(headers);
        setRawRows(rows);
        setAutoDetected(detected);
        setCheckResult(null);
        setImportResult(null);
        setDuplicateAction('skip');

        if (module === 'policies') {
          const userAseguradoras = user?.aseguradoras || [];
          if (userAseguradoras.length === 1) {
            setForcedAseguradora(userAseguradoras[0]);
            setColumnMap(map);
            setStep('mapping');
            setDialogOpen(true);
          } else {
            // admin (aseguradoras=[]) o agente con 2+ → siempre preguntar
            setPendingColumnMap(map);
            setShowAseguradoraSelector(true);
            setDialogOpen(true);
          }
        } else {
          setColumnMap(map);
          setStep('mapping');
          setDialogOpen(true);
        }
      } catch (err) {
        console.error('Error reading file:', err);
        toast.error('Error al leer el archivo. Verifique el formato.');
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  // ── Sheet selection → parse and go to mapping (sales-reports only) ─────────
  const handleSheetSelection = () => {
    if (importMode === 'single' && !manualMonth.trim()) {
      toast.error('Por favor especifica el mes del reporte.');
      return;
    }
    const sheetsToProcess = importMode === 'all' ? sheetNames : [selectedSheetName];
    const META_ADVISOR = '__meta_advisor_name__';
    const META_MONTH   = '__meta_report_month__';

    let combinedHeaders = null;
    let combinedRows    = [];
    let advisorValues   = [];
    let monthValues     = [];

    for (const sheetName of sheetsToProcess) {
      const ws = rawWorkbook.Sheets[sheetName];
      if (!ws) continue;
      const { headers, dataRows, advisorName } = parseSalesReportSheet(ws, sheetName);
      if (headers.length === 0 || dataRows.length === 0) continue;
      if (!combinedHeaders) combinedHeaders = headers;
      combinedRows.push(...dataRows);
      const resolvedAdvisor = isValidPersonName(advisorName) ? advisorName : (currentUserName || '');
      advisorValues.push(...dataRows.map(() => resolvedAdvisor));
      // For single mode: use the user-specified month. For all mode: detect per sheet.
      const sheetMonth = importMode === 'single' ? manualMonth.trim() : detectReportMonth(ws, sheetName);
      monthValues.push(...dataRows.map(() => sheetMonth));
    }

    if (!combinedHeaders || combinedRows.length === 0) {
      toast.error('No se encontraron datos válidos en las hojas seleccionadas.');
      return;
    }

    const extendedHeaders = [...combinedHeaders, META_ADVISOR, META_MONTH];
    const extendedRows    = combinedRows.map((row, i) => [
      ...row,
      advisorValues[i] || '',
      monthValues[i]   || '',
    ]);

    const { map, detected } = autoDetectMapping(combinedHeaders, 'sales-reports');
    map[META_ADVISOR] = 'advisor_name';
    map[META_MONTH]   = 'report_month';
    detected.add(META_ADVISOR);
    detected.add(META_MONTH);

    setRawHeaders(extendedHeaders);
    setRawRows(extendedRows);
    setColumnMap(map);
    setAutoDetected(detected);
    setStep('mapping');
  };

  // ── Step 1 → 2: apply mapping, deduplicate in-file, call check ────────────
  const handleMappingNext = async () => {
    // H: guardar mapeos confirmados en BD para próximas importaciones (silencioso)
    if (module === 'policies' && user?.role === 'admin' && apiUrl) {
      const aseg = forcedAseguradora || 'all';
      const mappingsToSave = {};
      for (const [col, fk] of Object.entries(columnMap)) {
        if (!col.startsWith('__meta_') && fk) mappingsToSave[col] = fk;
      }
      if (Object.keys(mappingsToSave).length > 0) {
        axios.post(`${apiUrl}/import/mappings`, { aseguradora: aseg, mappings: mappingsToSave })
          .catch(() => {});
      }
    }

    const rawRecords = applyMapping(rawRows, rawHeaders, columnMap);
    if (rawRecords.length === 0) {
      toast.error('No se detectaron registros válidos con el mapeo actual.');
      return;
    }
    const { records, mergedCount } = deduplicateInFile(rawRecords, module);
    setMappedRecords(records);
    setInFileMergedCount(mergedCount);
    setStep('preview');

    // Check duplicates if apiUrl provided
    if (apiUrl) {
      setLoading(true);
      try {
        const res = await axios.post(`${apiUrl}/${apiPath}/import`, {
          records,
          duplicate_action: 'check',
          ...(module === 'policies' && forcedAseguradora ? { forced_aseguradora: forcedAseguradora } : {}),
        });
        setCheckResult(res.data);
        // Store cross-month matches and init all decisions as 'seguimiento' by default
        const matches = res.data?.cross_month_matches || [];
        setCrossMonthMatches(matches);
        const initDecisions = {};
        matches.forEach(m => {
          const key = `${(m.client_name || '').toLowerCase().trim()}|${(m.new_month || '').toLowerCase().trim()}`;
          initDecisions[key] = 'seguimiento';
        });
        setCrossMonthDecisions(initDecisions);
      } catch (err) {
        console.error('Duplicate check error:', err);
        setCheckResult(null);
      } finally {
        setLoading(false);
      }
    }
  };

  // ── Step 2 → 3: confirm import ────────────────────────────────────────────
  const handleImport = async () => {
    setLoading(true);
    try {
      if (apiUrl) {
        let finalRecords = (module === 'policies' && forcedAseguradora)
          ? mappedRecords.map(r => ({ ...r, business_line: forcedAseguradora }))
          : mappedRecords;
        if (module === 'agents' && agentImportSede && agentImportSede !== 'multiple') {
          finalRecords = finalRecords.map(r => ({ ...r, sede: r.sede || agentImportSede }));
        }
        const payload = {
          records: finalRecords,
          duplicate_action: module === 'sales-reports' ? 'update' : duplicateAction,
        };
        if (module === 'sales-reports' && Object.keys(crossMonthDecisions).length > 0) {
          payload.cross_month_decisions = crossMonthDecisions;
        }
        const res = await axios.post(`${apiUrl}/${apiPath}/import`, payload);
        setImportResult(res.data);
        setResultCategory('imported');
        setStep('result');
        if (onSuccess) onSuccess(res.data);
        if (module === 'sales-reports') {
          toast.info(
            'Importación completada. Recuerda actualizar el Estado de cada contacto según el seguimiento realizado, ya que todos aparecen como Nuevo por defecto.',
            { duration: 10000 }
          );
        } else {
          const imp = res.data?.imported ?? 0;
          const upd = res.data?.updated ?? 0;
          toast.success(`✅ Importación completada — ${imp} nuevos, ${upd} actualizados.`, { duration: 7000 });
        }
      } else if (onImport) {
        // Legacy fallback
        await onImport(mappedRecords);
        toast.success(`${mappedRecords.length} registros importados`);
        handleClose();
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      console.error('Import error:', err);
      toast.error('Error al importar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAseguradoraSelected = (aseguradora) => {
    setShowAseguradoraSelector(false);
    setForcedAseguradora(aseguradora);
    setColumnMap(pendingColumnMap || {});
    setPendingColumnMap(null);
    setStep('mapping');
  };

  const handleClose = () => {
    setDialogOpen(false);
    setTimeout(() => {
      setStep('mapping');
      setRawWorkbook(null);
      setSheetNames([]);
      setSelectedSheetName('');
      setImportMode('single');
      setRawHeaders([]);
      setRawRows([]);
      setColumnMap({});
      setAutoDetected(new Set());
      setMappedRecords([]);
      setInFileMergedCount(0);
      setCheckResult(null);
      setImportResult(null);
      setMarkingOmitted(false);
      setOmittedMarked(false);
      setMarkedOmittedList([]);
      setManualMonth('');
      setCrossMonthMatches([]);
      setCrossMonthDecisions({});
      setShowAseguradoraSelector(false);
      setForcedAseguradora(null);
      setPendingColumnMap(null);
      setAgentImportSede(null);
    }, 200);
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    let exportData = data;
    if (fetchAllForExport) {
      try {
        toast.info('Cargando registros completos para exportar…', { duration: 3000 });
        exportData = await fetchAllForExport();
      } catch (err) {
        console.error('fetchAllForExport error:', err);
        toast.error('Error al cargar datos completos');
        return;
      }
    }
    if (!exportData?.length) {
      toast.error('No hay datos para exportar');
      return;
    }
    try {
      const rows = exportData.map(item => {
        const row = {};
        exportCfg.cols.forEach((col, i) => {
          let val = item[col];
          if (Array.isArray(val)) val = val.join(', ');
          row[exportCfg.headers[i]] = val ?? '';
        });
        return row;
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Datos');
      const dateStr = new Date().toISOString().split('T')[0];

      if (exportAsCsv) {
        const csv = XLSX.utils.sheet_to_csv(ws);
        saveAs(
          new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
          `${module}_export_${dateStr}.csv`
        );
      } else {
        ws['!cols'] = exportCfg.headers.map((h) => {
          const colData = [h, ...rows.map(r => String(r[h] || ''))];
          return { wch: Math.min(Math.max(...colData.map(s => s.length)) + 2, 50) };
        });
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(
          new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          `${module}_export_${dateStr}.xlsx`
        );
      }
      toast.success(`${exportData.length} registros exportados`);
      if (onExport) onExport();
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Error al exportar');
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  // Exclude hidden __meta_ pseudo-columns from counts/display
  const visibleHeaders = rawHeaders.filter(h => !h.startsWith('__meta_'));
  const mappedCount = Object.entries(columnMap).filter(([h, v]) => !h.startsWith('__meta_') && v).length;
  const previewCols = Object.entries(columnMap)
    .filter(([h, v]) => !h.startsWith('__meta_') && v)
    .slice(0, 5);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        onChange={handleFileSelect}
        className="hidden"
      />

      <Button
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        data-testid={`import-${module}-btn`}
        className="bg-[#1a365d] hover:bg-[#1a365d]/90 text-white"
      >
        <Upload className="h-4 w-4 mr-2" />
        Importar
      </Button>

      <Button
        onClick={handleExport}
        disabled={disabled || !data?.length}
        data-testid={`export-${module}-btn`}
        className="bg-[#1a365d] hover:bg-[#1a365d]/90 text-white"
      >
        <Download className="h-4 w-4 mr-2" />
        Exportar
      </Button>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">

          {/* ── STEP 0.5: Sede selector (agents only) ── */}
          {step === 'sede-select' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  ¿A qué sede pertenecen estos agentes?
                </DialogTitle>
                <DialogDescription>
                  Selecciona la sede para asignarla a todos los agentes del archivo,
                  o elige "Múltiples sedes" si los agentes pertenecen a distintas sedes
                  y deseas asignarlas manualmente.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {[
                  { key: 'Ciudad de México', label: 'Ciudad de México', cls: 'bg-orange-50 border-orange-300 text-orange-800 hover:bg-orange-100' },
                  { key: 'Guadalajara',       label: 'Guadalajara',       cls: 'bg-blue-50   border-blue-300   text-blue-800   hover:bg-blue-100'   },
                  { key: 'Querétaro',         label: 'Querétaro',         cls: 'bg-green-50  border-green-300  text-green-800  hover:bg-green-100'  },
                  { key: 'multiple',          label: 'Múltiples sedes',   cls: 'bg-gray-50   border-gray-300   text-gray-700   hover:bg-gray-100'   },
                ].map(({ key, label, cls }) => (
                  <button
                    key={key}
                    onClick={() => { setAgentImportSede(key); setStep('mapping'); }}
                    className={`border-2 rounded-lg p-5 flex flex-col items-center font-semibold transition-colors ${cls}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex justify-end pt-4 border-t">
                <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
              </div>
            </>
          )}

          {/* ── STEP 1.5: Aseguradora selector (policies only, when no aseguradora column) ── */}
          {showAseguradoraSelector && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  ¿A qué aseguradora pertenecen estas pólizas?
                </DialogTitle>
              </DialogHeader>
              <div className="p-2 flex flex-col gap-4">
                <p className="text-sm text-muted-foreground">
                  El archivo no contiene una columna de aseguradora ni de emisor.
                  Selecciona la aseguradora para aplicarla a todos los registros del archivo.
                </p>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {[
                    { key: 'allianz',  label: 'Allianz',       cls: 'bg-blue-50  border-blue-300  text-blue-800  hover:bg-blue-100' },
                    { key: 'qualitas', label: 'Quálitas',      cls: 'bg-green-50 border-green-300 text-green-800 hover:bg-green-100' },
                    { key: 'insignia', label: 'Insignia Life', cls: 'bg-red-50   border-red-300   text-red-800   hover:bg-red-100'  },
                  ].map(({ key, label, cls }) => (
                    <button
                      key={key}
                      onClick={() => handleAseguradoraSelected(key)}
                      className={`border-2 rounded-lg p-5 flex flex-col items-center font-semibold transition-colors ${cls}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex justify-end pt-2 border-t">
                  <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
                </div>
              </div>
            </>
          )}

          {/* ── STEP 0: Sheet selection (sales-reports only) ── */}
          {step === 'sheet-select' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  ¿Qué deseas importar?
                </DialogTitle>
                <DialogDescription>
                  El archivo contiene <strong>{sheetNames.length}</strong> hoja{sheetNames.length !== 1 ? 's' : ''}.
                  Selecciona si importas un mes específico o todo el archivo.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="flex flex-col gap-3">
                  <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${importMode === 'single' ? 'border-[#1a365d] bg-blue-50 dark:bg-blue-900/20' : 'border-border hover:bg-muted/40'}`}>
                    <input
                      type="radio"
                      className="mt-1"
                      name="importMode"
                      value="single"
                      checked={importMode === 'single'}
                      onChange={() => setImportMode('single')}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Solo este mes</div>
                      <div className="text-xs text-muted-foreground mb-2">Importa una hoja específica del archivo</div>
                      {importMode === 'single' && (
                        <>
                          <select
                            value={selectedSheetName}
                            onChange={e => {
                              setSelectedSheetName(e.target.value);
                              const ws = rawWorkbook?.Sheets[e.target.value];
                              if (ws) setManualMonth(detectReportMonth(ws, e.target.value));
                            }}
                            className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                            onClick={e => e.stopPropagation()}
                          >
                            {sheetNames.map(name => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                          <div className="mt-3">
                            <div className="text-xs font-medium mb-1">Mes del reporte</div>
                            <input
                              type="text"
                              value={manualMonth}
                              onChange={e => setManualMonth(e.target.value)}
                              placeholder="Ej: Marzo 2026"
                              className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                              onClick={e => e.stopPropagation()}
                            />
                            <div className="text-xs text-muted-foreground mt-1">Detectado automáticamente — puedes editar</div>
                          </div>
                        </>
                      )}
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${importMode === 'all' ? 'border-[#1a365d] bg-blue-50 dark:bg-blue-900/20' : 'border-border hover:bg-muted/40'}`}>
                    <input
                      type="radio"
                      className="mt-1"
                      name="importMode"
                      value="all"
                      checked={importMode === 'all'}
                      onChange={() => setImportMode('all')}
                    />
                    <div>
                      <div className="font-medium text-sm">Todo el archivo</div>
                      <div className="text-xs text-muted-foreground">
                        Importa todas las hojas: {sheetNames.join(', ')}.
                        Duplicados entre hojas se fusionan automáticamente.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={handleClose} className="border-gray-300 text-gray-700">Cancelar</Button>
                <Button
                  onClick={handleSheetSelection}
                  className="bg-[#1a365d] hover:bg-[#1a365d]/90 text-white"
                >
                  Continuar
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </>
          )}

          {/* ── STEP 1: Column Mapping ── */}
          {step === 'mapping' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Mapeo de columnas
                </DialogTitle>
                <DialogDescription>
                  Se encontraron <strong>{visibleHeaders.length}</strong> columnas en el archivo.{' '}
                  <strong>{mappedCount}</strong> detectadas automáticamente.
                  Ajusta el mapeo si es necesario.
                </DialogDescription>
              </DialogHeader>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/2">Columna del archivo</TableHead>
                      <TableHead>Campo del CRM</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleHeaders.map(header => (
                      <TableRow key={header}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {header}
                            {autoDetected.has(header) && (
                              <Badge variant="secondary" className="text-xs">Auto</Badge>
                            )}
                            {fuzzyMatched.has(header) && !autoDetected.has(header) && (
                              <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300">Sugerido</Badge>
                            )}
                          </div>
                          {/* Show sample value */}
                          {rawRows[0]?.[rawHeaders.indexOf(header)] !== undefined && (
                            <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[220px]">
                              Ej: {String(rawRows[0][rawHeaders.indexOf(header)] ?? '')}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <select
                            value={columnMap[header] || ''}
                            onChange={e => setColumnMap(prev => ({ ...prev, [header]: e.target.value || null }))}
                            className="w-full border rounded px-2 py-1 text-sm bg-background"
                          >
                            <option value="">— No importar —</option>
                            {module === 'policies' && dynamicFieldOptions ? (
                              /* H: opciones dinámicas desde BD */
                              <>
                                <optgroup label="Póliza">
                                  {dynamicFieldOptions.standard.map(({ key, label }) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </optgroup>
                                {dynamicFieldOptions.custom.length > 0 && (
                                  <optgroup label="Campos personalizados">
                                    {dynamicFieldOptions.custom.map(({ key, label }) => (
                                      <option key={key} value={key}>{label}</option>
                                    ))}
                                  </optgroup>
                                )}
                              </>
                            ) : module === 'policies' ? (
                              /* Fallback: FIELD_LABELS hardcodeado */
                              <>
                                <optgroup label="Campos de la póliza">
                                  {Object.entries(fieldLabels)
                                    .filter(([key]) => !key.startsWith('custom:'))
                                    .map(([key, label]) => (
                                      <option key={key} value={key}>{label}</option>
                                    ))
                                  }
                                </optgroup>
                                <optgroup label="Campos personalizados">
                                  {Object.entries(fieldLabels)
                                    .filter(([key]) => key.startsWith('custom:'))
                                    .map(([key, label]) => (
                                      <option key={key} value={key}>{label}</option>
                                    ))
                                  }
                                </optgroup>
                              </>
                            ) : (
                              <>
                                {Object.entries(fieldLabels).map(([key, label]) => (
                                  <option key={key} value={key}>{label}</option>
                                ))}
                                {module === 'leads' && dynamicFieldOptions?.custom?.length > 0 && (
                                  <optgroup label="Campos personalizados">
                                    {dynamicFieldOptions.custom.map(({ key, label }) => (
                                      <option key={key} value={key}>{label}</option>
                                    ))}
                                  </optgroup>
                                )}
                              </>
                            )}
                          </select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={handleClose} className="border-gray-300 text-gray-700">Cancelar</Button>
                <Button
                  onClick={handleMappingNext}
                  disabled={mappedCount === 0}
                  className="bg-[#1a365d] hover:bg-[#1a365d]/90 text-white"
                >
                  Continuar
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </>
          )}

          {/* ── STEP 2: Preview + Duplicate Check ── */}
          {step === 'preview' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Vista previa e importación
                </DialogTitle>
                <DialogDescription>
                  {mappedRecords.length} registros listos para importar.
                </DialogDescription>
              </DialogHeader>

              {/* In-file merge notice */}
              {inFileMergedCount > 0 && (
                <div className="p-3 rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-700 dark:text-blue-300">
                  Se encontraron <strong>{inFileMergedCount}</strong> duplicados en el archivo, se fusionaron automáticamente.
                </div>
              )}

              {/* Duplicate check result */}
              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/40 rounded-lg">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Verificando duplicados…
                </div>
              )}

              {!loading && checkResult && (
                <div className="flex flex-col gap-2">
                  {/* Resumen de conteos */}
                  <div className={`p-3 rounded-lg border ${checkResult.duplicate_count > 0 ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20' : 'border-green-300 bg-green-50 dark:bg-green-900/20'}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="text-sm">
                        <span className="font-semibold text-green-700 dark:text-green-400">
                          {checkResult.new_count} nuevos
                        </span>
                        {checkResult.duplicate_count > 0 && (
                          <>
                            {' · '}
                            <span className="font-semibold text-amber-700 dark:text-amber-400">
                              {checkResult.duplicate_count} duplicados
                            </span>
                            {checkResult.duplicates?.length > 0 && (
                              <span className="text-muted-foreground ml-1">
                                ({checkResult.duplicates.slice(0, 3).map(d => d.client_name || d.name).join(', ')}{checkResult.duplicates.length > 3 ? '…' : ''})
                              </span>
                            )}
                          </>
                        )}
                        {checkResult.renewed_count > 0 && (
                          <>
                            {' · '}
                            <span className="font-semibold text-blue-700 dark:text-blue-400">
                              {checkResult.renewed_count} renovaciones
                            </span>
                          </>
                        )}
                        {checkResult.omitted_count > 0 && (
                          <>
                            {' · '}
                            <span className="font-semibold text-red-700 dark:text-red-400">
                              {checkResult.omitted_count} omitidas
                            </span>
                          </>
                        )}
                        {checkResult.current_retail_count != null && module === 'policies' && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            Total Retail tras importar: ~{checkResult.current_retail_count + checkResult.new_count}
                          </div>
                        )}
                      </div>
                      {checkResult.duplicate_count > 0 && module !== 'sales-reports' && (
                        <div className="flex gap-2">
                          <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition-colors ${duplicateAction === 'update' ? 'bg-[#1a365d] border-[#1a365d] text-white' : 'border-border hover:bg-muted'}`}>
                            <input type="radio" className="hidden" name="dupAction" value="update" checked={duplicateAction === 'update'} onChange={() => setDuplicateAction('update')} />
                            Actualizar duplicados
                          </label>
                          <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition-colors ${duplicateAction === 'skip' ? 'bg-[#1a365d] border-[#1a365d] text-white' : 'border-border hover:bg-muted'}`}>
                            <input type="radio" className="hidden" name="dupAction" value="skip" checked={duplicateAction === 'skip'} onChange={() => setDuplicateAction('skip')} />
                            Ignorar duplicados
                          </label>
                        </div>
                      )}
                      {checkResult.duplicate_count > 0 && module === 'sales-reports' && (
                        <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-3 py-1.5 rounded-lg">
                          Los duplicados se fusionarán — se actualizarán sus datos y se agregarán negocios nuevos
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bloque de omitidas — oculto por completo en archivos 100% Inversión (SVIP/OPPT):
                      ese archivo nunca trae pólizas Retail, así que el diff siempre mostraría
                      TODAS las Retail como "omitidas" — falsa alarma, no un dato real. */}
                  {checkResult.omitted_count > 0 && module === 'policies' && !checkResult.es_archivo_inversion && (
                    <div className="p-3 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 text-sm">
                      <p className="text-red-800 dark:text-red-300 font-medium mb-2">
                        ⚠ {checkResult.omitted_count} póliza{checkResult.omitted_count !== 1 ? 's' : ''} existen en el sistema pero no aparecen en este archivo.
                        Posiblemente fueron canceladas o no renovadas por el cliente.
                      </p>
                      {checkResult.omitted_preview?.length > 0 && (
                        <ul className="text-red-700 dark:text-red-400 text-xs space-y-0.5 mb-3 ml-2 max-h-64 overflow-y-auto">
                          {checkResult.omitted_preview.map((p, i) => (
                            <li key={i}>• {p.policy_number} — {p.client_name}</li>
                          ))}
                        </ul>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {checkResult.omitted_preview?.length > 0 && (
                          <button
                            onClick={() => {
                              const csv = 'policy_number,client_name\n' + checkResult.omitted_preview
                                .map(p => `"${String(p.policy_number ?? '').replace(/"/g, '""')}","${String(p.client_name ?? '').replace(/"/g, '""')}"`)
                                .join('\n');
                              const dateStr = new Date().toISOString().split('T')[0];
                              saveAs(
                                new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
                                `polizas_omitidas_${dateStr}.csv`
                              );
                            }}
                            className="px-3 py-1.5 rounded-lg border border-red-400 bg-white dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                          >
                            Exportar lista a CSV
                          </button>
                        )}
                        {omittedMarked ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium">
                            ✓ Marcadas como omitidas
                          </span>
                        ) : (
                          <button
                            onClick={async () => {
                              if (!apiUrl || !checkResult.omitted_policy_ids?.length) return;
                              setMarkingOmitted(true);
                              try {
                                const res = await axios.post(`${apiUrl}/policies/mark-omitted`, {
                                  policy_ids: checkResult.omitted_policy_ids,
                                });
                                setOmittedMarked(true);
                                setMarkedOmittedList(res.data?.marked_policies || []);
                                toast.success(`${checkResult.omitted_count} pólizas marcadas como omitidas`);
                              } catch {
                                toast.error('Error al marcar pólizas omitidas');
                              } finally {
                                setMarkingOmitted(false);
                              }
                            }}
                            disabled={markingOmitted}
                            className="px-3 py-1.5 rounded-lg border border-red-400 bg-white dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50"
                          >
                            {markingOmitted ? 'Marcando…' : `Marcar ${checkResult.omitted_count} como omitidas`}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Desglose directo del archivo (pre-BD): total filas, únicas (Retail) y
                      duplicados internos — mismo policy_number 2+ veces en el propio Excel
                      (típicamente año anterior + año nuevo, CATEGORÍA 1 de RENOVACIONES.md) */}
                  {checkResult.file_duplicates_count > 0 && module === 'policies' && (
                    <div className="p-3 rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/20 text-sm">
                      <p className="text-blue-800 dark:text-blue-300 font-medium mb-2">
                        📄 Archivo: {checkResult.file_total_rows} filas · {checkResult.file_retail_count} pólizas únicas (Retail)
                        {' · '}
                        <span className="font-semibold">{checkResult.file_duplicates_count} pólizas duplicadas (total mostradas: {checkResult.file_duplicates_preview?.length ?? 0})</span>
                      </p>
                      {checkResult.file_duplicates_preview?.length > 0 && (
                        <ul className="text-blue-700 dark:text-blue-400 text-xs space-y-0.5 mb-3 ml-2 max-h-64 overflow-y-auto">
                          {checkResult.file_duplicates_preview.map((p, i) => (
                            <li key={i}>
                              • {p.policy_number} — {p.client_name || 'sin nombre'} ({p.emisor || '—'}, {p.start_date || 'sin fecha'})
                              {!p.mismo_emisor && (
                                <span className="text-amber-600 dark:text-amber-400"> ⚠ emisor distinto</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {checkResult.file_duplicates_preview?.length > 0 && (
                        <button
                          onClick={() => {
                            const csv = 'policy_number,client_name,emisor,start_date,mismo_emisor\n' + checkResult.file_duplicates_preview
                              .map(p => `"${String(p.policy_number ?? '').replace(/"/g, '""')}","${String(p.client_name ?? '').replace(/"/g, '""')}","${String(p.emisor ?? '').replace(/"/g, '""')}","${String(p.start_date ?? '').replace(/"/g, '""')}","${p.mismo_emisor}"`)
                              .join('\n');
                            const dateStr = new Date().toISOString().split('T')[0];
                            saveAs(
                              new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
                              `polizas_duplicadas_archivo_${dateStr}.csv`
                            );
                          }}
                          className="px-3 py-1.5 rounded-lg border border-blue-400 bg-white dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                        >
                          Exportar lista a CSV
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Preview table */}
              {mappedRecords.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">#</TableHead>
                        {previewCols.map(([rawH, fieldKey]) => (
                          <TableHead key={fieldKey}>{fieldLabels[fieldKey] || fieldKey}</TableHead>
                        ))}
                        {previewCols.length < Object.values(columnMap).filter(Boolean).length && (
                          <TableHead className="text-muted-foreground">…</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mappedRecords.slice(0, 10).map((row, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                          {previewCols.map(([, fieldKey]) => (
                            <TableCell key={fieldKey} className="max-w-[180px] truncate text-sm">
                              {Array.isArray(row[fieldKey])
                                ? row[fieldKey].join(', ')
                                : String(row[fieldKey] ?? '—')}
                            </TableCell>
                          ))}
                          {previewCols.length < Object.values(columnMap).filter(Boolean).length && (
                            <TableCell className="text-muted-foreground">…</TableCell>
                          )}
                        </TableRow>
                      ))}
                      {mappedRecords.length > 10 && (
                        <TableRow>
                          <TableCell colSpan={previewCols.length + 2} className="text-center text-sm text-muted-foreground py-2">
                            …y {mappedRecords.length - 10} registros más
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}

              {mappedRecords.length > 500 && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-300">
                  <span className="text-base leading-none mt-0.5">⚠️</span>
                  <span>
                    <strong>Archivo grande detectado ({mappedRecords.length} registros).</strong>{' '}
                    El proceso puede tardar varios minutos. Por favor no cierres esta ventana.
                  </span>
                </div>
              )}

              <div className="flex justify-between gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setStep(module === 'sales-reports' ? 'sheet-select' : 'mapping')} disabled={loading} className="border-gray-300 text-gray-700">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Volver
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleClose} disabled={loading} className="border-gray-300 text-gray-700">Cancelar</Button>
                  <Button
                    onClick={() => {
                      if (module === 'sales-reports' && crossMonthMatches.length > 0) {
                        setStep('cross-month-review');
                      } else {
                        handleImport();
                      }
                    }}
                    disabled={loading || mappedRecords.length === 0}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {loading ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    {loading
                      ? 'Verificando…'
                      : (module === 'sales-reports' && crossMonthMatches.length > 0)
                        ? `Revisar seguimientos (${crossMonthMatches.length})`
                        : `Importar ${mappedRecords.length} registros`}
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* ── STEP 2.5: Cross-month review (sales-reports only) ── */}
          {step === 'cross-month-review' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  Revisión de seguimientos
                </DialogTitle>
                <DialogDescription>
                  Se encontraron {crossMonthMatches.length} registro(s) que ya existen en meses anteriores con el mismo negocio.
                  Indica qué hacer con cada uno.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 max-h-[50vh] overflow-y-auto py-1 pr-1">
                {crossMonthMatches.map((match, i) => {
                  const key = `${(match.client_name || '').toLowerCase().trim()}|${(match.new_month || '').toLowerCase().trim()}`;
                  const decision = crossMonthDecisions[key] || 'seguimiento';
                  return (
                    <div key={i} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-sm">{match.client_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Negocio: <span className="font-medium">{match.business_type}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Ya existe en <span className="font-medium">{match.existing_month}</span> → importando para <span className="font-medium">{match.new_month}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setCrossMonthDecisions(prev => ({ ...prev, [key]: 'seguimiento' }))}
                          className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                            decision === 'seguimiento'
                              ? 'bg-[#1a365d] border-[#1a365d] text-white'
                              : 'border-border hover:bg-muted text-foreground'
                          }`}
                        >
                          Es un seguimiento
                          <span className="block font-normal opacity-80">Vincular al mismo lead, no duplicar</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCrossMonthDecisions(prev => ({ ...prev, [key]: 'nuevo' }))}
                          className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                            decision === 'nuevo'
                              ? 'bg-[#1a365d] border-[#1a365d] text-white'
                              : 'border-border hover:bg-muted text-foreground'
                          }`}
                        >
                          Es un negocio nuevo
                          <span className="block font-normal opacity-80">Crear registro independiente</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setStep('preview')} disabled={loading} className="border-gray-300 text-gray-700">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Volver
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleClose} disabled={loading} className="border-gray-300 text-gray-700">Cancelar</Button>
                  <Button
                    onClick={handleImport}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    {loading ? 'Importando…' : `Importar ${mappedRecords.length} registros`}
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* ── STEP 3: Result ── */}
          {step === 'result' && importResult && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-500" />
                  Importación completada
                </DialogTitle>
              </DialogHeader>

              {module === 'policies' ? (
                <>
                  <p className="text-sm text-muted-foreground -mt-1">
                    Selecciona una categoría para ver el detalle de las pólizas
                  </p>

                  <div className="grid grid-cols-5 gap-3 py-4">
                    {RESULT_CATEGORIES.map(cat => {
                      // 'omitted' es la única categoría que no viene de importResult: solo cuenta lo que
                      // el usuario marcó explícitamente con el botón "Marcar como omitidas" en el preview.
                      const count = cat.key === 'omitted'
                        ? markedOmittedList.length
                        : (importResult[RESULT_COUNT_KEYS[cat.key]] ?? 0);
                      const isSelected = resultCategory === cat.key;
                      return (
                        <button
                          key={cat.key}
                          type="button"
                          onClick={() => setResultCategory(cat.key)}
                          className={`text-center p-4 rounded-lg border transition-colors ${cat.base} ${isSelected ? `ring-2 ring-offset-1 dark:ring-offset-gray-900 ${cat.ring}` : 'opacity-70 hover:opacity-100'}`}
                        >
                          <div className={`text-3xl font-bold ${cat.text}`}>{count}</div>
                          <div className={`text-sm mt-1 ${cat.text}`}>{cat.label}</div>
                        </button>
                      );
                    })}
                  </div>

                  {(() => {
                    const activeCat = RESULT_CATEGORIES.find(c => c.key === resultCategory);
                    const list = resultCategory === 'omitted'
                      ? markedOmittedList
                      : (activeCat?.listKey ? (importResult[activeCat.listKey] || []) : []);
                    if (resultCategory !== 'omitted' && !activeCat?.listKey) {
                      return (
                        <div className="p-4 text-center text-sm text-muted-foreground border rounded-lg border-dashed">
                          Sin detalle disponible para esta categoría.
                        </div>
                      );
                    }
                    if (list.length === 0) {
                      return (
                        <div className="p-4 text-center text-sm text-muted-foreground border rounded-lg border-dashed">
                          No hay pólizas en esta categoría.
                        </div>
                      );
                    }
                    return (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="max-h-[300px] overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-background">
                              <tr className="text-left text-muted-foreground border-b">
                                <th className="p-2">Póliza #</th>
                                <th className="p-2">Cliente</th>
                                <th className="p-2">Agente</th>
                                <th className="p-2">Emisor</th>
                                <th className="p-2">Aseguradora</th>
                              </tr>
                            </thead>
                            <tbody>
                              {list.map((p, i) => (
                                <tr key={i} className="border-b border-dashed last:border-0">
                                  <td className="p-2">{p.policy_number || '—'}</td>
                                  <td className="p-2">{p.client_name || '—'}</td>
                                  <td className="p-2">{p.agent_name || '—'}</td>
                                  <td className="p-2">{p.emisor || '—'}</td>
                                  <td className="p-2">{p.business_line || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="grid grid-cols-3 gap-3 py-4">
                  <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="text-3xl font-bold text-green-700 dark:text-green-400">{importResult.imported}</div>
                    <div className="text-sm text-green-600 dark:text-green-500 mt-1">Importados</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="text-3xl font-bold text-blue-700 dark:text-blue-400">{importResult.updated}</div>
                    <div className="text-sm text-blue-600 dark:text-blue-500 mt-1">Actualizados</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                    <div className="text-3xl font-bold text-gray-700 dark:text-gray-300">{importResult.skipped}</div>
                    <div className="text-sm text-muted-foreground mt-1">Ignorados</div>
                  </div>
                </div>
              )}

              {(importResult.renewed ?? 0) > 0 && (
                <div className="p-3 rounded-lg border border-purple-300 bg-purple-50 dark:bg-purple-900/20 text-sm text-purple-700 dark:text-purple-300">
                  🔄 {importResult.renewed} póliza(s) marcadas como renovadas (año anterior → renewed).
                </div>
              )}

              {inFileMergedCount > 0 && (
                <div className="p-3 rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-700 dark:text-blue-300">
                  {inFileMergedCount} duplicados del archivo se fusionaron antes de importar.
                </div>
              )}

              {importResult.errors?.length > 0 && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-medium text-sm mb-2">
                    <AlertCircle className="h-4 w-4" />
                    {importResult.errors.length} error(es)
                  </div>
                  <ul className="text-xs text-red-600 dark:text-red-300 list-disc list-inside space-y-1 max-h-24 overflow-y-auto">
                    {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}

              <div className="flex justify-end pt-2 border-t">
                <Button onClick={() => { handleClose(); if (onSuccess) onSuccess(); }} className="bg-[#1a365d] hover:bg-[#1a365d]/90 text-white">
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImportExportManager;
